/**
 * CRM-LIST-VIEWS-V1 (V1-P7) -- ViewChips: the reusable named-view chip row
 * (registered shared component; token layer only).
 *
 * Renders the consumer's ROLE-DEFAULT view presets (C-11) as chips, the
 * active state (an exactly-matching preset highlights; a modified state
 * renders the honest "Custom" chip), and the copy/bookmark-URL door -- the
 * URL is the canonical view-state representation (C-9; URL-only persistence
 * per the C-2 identity stop gate; the database rung is the named
 * USER-PREFERENCE-STORE-V1 seam).
 */

export interface ViewChipDef {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
}

export interface ViewChipsProps {
  readonly views: readonly ViewChipDef[];
  /** The exactly-matching view id, or null (renders the Custom chip). */
  readonly activeViewId: string | null;
  readonly onSelect: (id: string) => void;
  /** Copy the canonical URL of the current state (bookmarkable). */
  readonly onCopyUrl: () => void;
  /** True briefly after a successful copy (renders the confirmation). */
  readonly copied?: boolean;
  readonly showCopyLink?: boolean;
}

export default function ViewChips({
  views,
  activeViewId,
  onSelect,
  onCopyUrl,
  copied = false,
  showCopyLink = true,
}: ViewChipsProps): JSX.Element {
  return (
    <div className="ac-viewchips" role="tablist" aria-label="List views" data-testid="ac-viewchips">
      {views.map((v) => (
        <button
          key={v.id}
          type="button"
          role="tab"
          aria-selected={v.id === activeViewId}
          title={v.hint}
          className={
            v.id === activeViewId
              ? 'ac-viewchips__chip ac-viewchips__chip--active'
              : 'ac-viewchips__chip'
          }
          data-testid={`ac-viewchip-${v.id}`}
          onClick={() => onSelect(v.id)}
        >
          {v.label}
        </button>
      ))}
      {activeViewId === null ? (
        <span
          className="ac-viewchips__chip ac-viewchips__chip--custom"
          data-testid="ac-viewchip-custom"
        >
          Custom
        </span>
      ) : null}
      {showCopyLink ? <button
        type="button"
        className="ac-viewchips__copy"
        data-testid="ac-viewchips-copy"
        onClick={onCopyUrl}
      >
        {copied ? 'Link copied' : 'Copy link to this view'}
      </button> : null}
    </div>
  );
}
